import React, { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { Popover, Transition } from "@headlessui/react";
import { ChevronDownIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { CustomerSelector } from "../moduleSelectors/customerSelector";
import { ContactsSelector } from "../moduleSelectors/contactSelector";
import { ReportSelector } from "../moduleSelectors/reportSelector";
import { ProjectSelector } from "../moduleSelectors/projectSelector";
import { fetchQualitys } from "../../../api/quality/quality";
import { fetchQualityInstructions } from "../../../api/quality/instructions";

// Quality Instruction Selector Component
const QualityInstructionSelector = ({ open, onClose, onChange }) => {
  const { t } = useTranslation();
  const [instructions, setInstructions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  React.useEffect(() => {
    if (open) {
      setIsLoading(true);
      fetchQualityInstructions({ per_page: 100 })
        .then((res) => {
          setInstructions(res?.data?.data || res?.data || []);
        })
        .catch((err) => {
          console.error("Error fetching quality instructions:", err);
          setInstructions([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">{t("Select Quality Instruction")}</h3>
          <button 
            onClick={() => onClose()} 
            className="text-gray-400 hover:text-gray-600"
            type="button"
          >
            <XCircleIcon className="h-6 w-6" />
          </button>
        </div>
        {isLoading ? (
          <div className="text-center py-4">{t("Loading...")}</div>
        ) : (
          <div className="space-y-2">
            {instructions.map((inst) => (
              <button
                key={inst.id}
                onClick={() => {
                  onChange({
                    target: {
                      value: inst.id,
                      data: { object: inst },
                    },
                  });
                  onClose();
                }}
                className="w-full text-left p-3 border rounded hover:bg-gray-50"
              >
                <div className="font-medium">{inst.title}</div>
                {inst.text && (
                  <div className="text-sm text-gray-500 truncate">{inst.text}</div>
                )}
              </button>
            ))}
            {instructions.length === 0 && (
              <div className="text-center py-4 text-gray-500">
                {t("No quality instructions found")}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Quality Selector Component
const QualitySelector = ({ open, onClose, onChange }) => {
  const { t } = useTranslation();
  const [qualities, setQualities] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  React.useEffect(() => {
    if (open) {
      setIsLoading(true);
      fetchQualitys({ per_page: 100 })
        .then((res) => {
          // Handle different response structures
          const data = res?.data || {};
          const qualityList = Array.isArray(data) ? data : Object.values(data).flat();
          setQualities(qualityList);
        })
        .catch((err) => {
          console.error("Error fetching qualities:", err);
          setQualities([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">{t("Select Quality Routine")}</h3>
          <button 
            onClick={() => onClose()} 
            className="text-gray-400 hover:text-gray-600"
            type="button"
          >
            <XCircleIcon className="h-6 w-6" />
          </button>
        </div>
        {isLoading ? (
          <div className="text-center py-4">{t("Loading...")}</div>
        ) : (
          <div className="space-y-2">
            {qualities.map((quality) => (
              <button
                key={quality.id}
                onClick={() => {
                  onChange({
                    target: {
                      value: quality.id,
                      data: { object: quality },
                    },
                  });
                  onClose();
                }}
                className="w-full text-left p-3 border rounded hover:bg-gray-50"
              >
                <div className="font-medium">{quality.title}</div>
                {quality.text && (
                  <div className="text-sm text-gray-500 truncate">{quality.text}</div>
                )}
              </button>
            ))}
            {qualities.length === 0 && (
              <div className="text-center py-4 text-gray-500">
                {t("No quality routines found")}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Main ModuleAttachment Component
export const ModuleAttachment = ({ 
  tags = [], 
  onTagsChange, 
  allowedModules = ["hse_quality", "hse_instruction", "report", "contact", "customer", "project"],
  showField = true 
}) => {
  const { t } = useTranslation();
  const [openModuleDialog, setOpenModuleDialog] = useState(null);

  const moduleTags = [
    ...(allowedModules.includes("hse_quality") ? [{ name: t("Quality Routine"), action: () => setOpenModuleDialog("hse_quality"), module: "hse_quality" }] : []),
    ...(allowedModules.includes("hse_instruction") ? [{ name: t("Quality Instruction"), action: () => setOpenModuleDialog("hse_instruction"), module: "hse_instruction" }] : []),
    ...(allowedModules.includes("report") ? [{ name: t("Report"), action: () => setOpenModuleDialog("report"), module: "report" }] : []),
    ...(allowedModules.includes("contact") ? [{ name: t("Contact"), action: () => setOpenModuleDialog("contact"), module: "contact" }] : []),
    ...(allowedModules.includes("customer") ? [{ name: t("Customer"), action: () => setOpenModuleDialog("customer"), module: "customer" }] : []),
    ...(allowedModules.includes("project") ? [{ name: t("Project"), action: () => setOpenModuleDialog("project"), module: "project" }] : []),
  ];

  const handleModuleDialogSelectorChange = (type, e) => {
    const payload = {
      module: type,
      module_param: e?.target?.value || e?.target?.data?.object?.id,
      object: e?.target?.data?.object,
    };

    // Add as tag
    const newTags = [...tags, payload];
    onTagsChange(newTags);
    setOpenModuleDialog(null);
  };

  const handleDeleteTag = (idx) => () => {
    const newTags = tags.filter((_, i) => i !== idx);
    onTagsChange(newTags);
  };

  const tagColor = (tag) => {
    switch (tag?.module) {
      case "hse_quality":
        return "bg-purple-100 text-purple-800";
      case "hse_instruction":
        return "bg-indigo-100 text-indigo-800";
      case "report":
        return "bg-blue-100 text-blue-800";
      case "contact":
        return "bg-green-100 text-green-800";
      case "customer":
        return "bg-red-100 text-black";
      case "project":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const TagTitle = (tag) => {
    if (tag?.object?.title) {
      return tag.object.title;
    }
    if (tag?.object?.displayTitle) {
      return tag.object.displayTitle;
    }
    return `${tag.module} ${tag.module_param}`;
  };

  return (
    <>
      {showField && (
        <Popover className="relative">
          <Popover.Button className="inline-flex items-center gap-x-1 text-md px-2 leading-6 text-blue-400 hover:text-blue-600">
            <span>{t("Attach")}</span>
            <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
          </Popover.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className="absolute left-1/2 z-10 mt-5 flex w-screen max-w-min -translate-x-1/2 px-4">
              <div className="w-56 shrink rounded-xl bg-white p-4 text-sm font-semibold leading-6 text-gray-900 shadow-lg ring-1 ring-gray-900/5">
                {moduleTags.map((item) => (
                  <a
                    key={item.name}
                    onClick={item.action}
                    className="block p-2 hover:text-blue-400 cursor-pointer"
                  >
                    {item.name}
                  </a>
                ))}
              </div>
            </Popover.Panel>
          </Transition>
        </Popover>
      )}

      {/* Quality Instruction Selector */}
      {openModuleDialog === "hse_instruction" && (
        <QualityInstructionSelector
          open={true}
          onClose={() => setOpenModuleDialog(null)}
          onChange={(data) => handleModuleDialogSelectorChange("hse_instruction", data)}
        />
      )}

      {/* Quality Selector */}
      {openModuleDialog === "hse_quality" && (
        <QualitySelector
          open={true}
          onClose={() => setOpenModuleDialog(null)}
          onChange={(data) => handleModuleDialogSelectorChange("hse_quality", data)}
        />
      )}

      {/* Existing selectors */}
      <ContactsSelector
        showField={false}
        open={openModuleDialog === "contact"}
        onClose={(state) => {
          if (!state) setOpenModuleDialog(null);
        }}
        onChange={(data) => handleModuleDialogSelectorChange("contact", data)}
      />

      <CustomerSelector
        showField={false}
        open={openModuleDialog === "customer"}
        onClose={(state) => {
          if (!state) setOpenModuleDialog(null);
        }}
        onChange={(data) => handleModuleDialogSelectorChange("customer", data)}
      />

      <ReportSelector
        showField={false}
        open={openModuleDialog === "report"}
        onClose={(state) => {
          if (!state) setOpenModuleDialog(null);
        }}
        onChange={(data) => handleModuleDialogSelectorChange("report", data)}
      />

      <ProjectSelector
        showField={false}
        open={openModuleDialog === "project"}
        onClose={(state) => {
          if (!state) setOpenModuleDialog(null);
        }}
        onChange={(data) => handleModuleDialogSelectorChange("project", data)}
      />

      {/* Display tags */}
      {tags && tags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {tags.map((tag, idx) => (
            <span
              key={idx}
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${tagColor(tag)}`}
            >
              <span>{TagTitle(tag)}</span>
              <button
                type="button"
                className="ml-2 cursor-pointer"
                onClick={handleDeleteTag(idx)}
              >
                <XCircleIcon className="h-4 w-4" />
              </button>
            </span>
          ))}
        </div>
      )}
    </>
  );
};

export default ModuleAttachment;

